import type { ReactElement } from "react";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { useReportCurrency } from "@/profitability/currency";
import type { TemplateSetting } from "./types";
import { formatScalar, formatSetting } from "./values";

const mapEntries = (setting: TemplateSetting) => {
  if (
    setting.type !== "map" ||
    !setting.value ||
    typeof setting.value !== "object"
  )
    return null;
  const entries = Object.entries(setting.value);
  return entries.length > 0 ? entries : null;
};

/**
 * The template settings a run's script read, with the value each had for
 * this run, opened from `trigger`: what makes a result reproducible after
 * the template changes. A title and what it is, a row a setting, and how a
 * changed setting reaches this period.
 */
export const SettingsUsed = ({
  settings,
  trigger,
}: {
  settings: TemplateSetting[];
  /** The button that opens it, as Base UI renders a trigger. */
  trigger: ReactElement;
}) => {
  const currency = useReportCurrency();
  return (
    <Popover>
      <PopoverTrigger render={trigger} />
      <PopoverContent align="start" className="w-80 gap-0 p-0">
        <PopoverHeader className="px-4 pt-3.5 pb-3">
          <PopoverTitle>Settings used</PopoverTitle>
          <PopoverDescription>
            The values this run read from the agent&apos;s settings.
          </PopoverDescription>
        </PopoverHeader>
        <dl className="max-h-80 divide-y divide-line overflow-y-auto overscroll-contain border-y border-line px-4">
          {settings.map((setting) => {
            const entries = mapEntries(setting);
            return entries ? (
              // A map reads as a small table of its own: one entry a line.
              <div key={setting.key} className="py-2">
                <dt className="text-subtle">{setting.label}</dt>
                <dd>
                  <dl className="mt-1 space-y-0.5">
                    {entries.map(([name, entry]) => (
                      <div
                        key={name}
                        className="flex items-baseline justify-between gap-4 ps-3"
                      >
                        <dt className="min-w-0">{name}</dt>
                        <dd className="text-right font-medium tabular-nums">
                          {formatScalar(
                            setting.value_type ?? "number",
                            entry,
                            currency,
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </dd>
              </div>
            ) : (
              <div
                key={setting.key}
                className="flex items-baseline justify-between gap-4 py-2"
              >
                <dt className="min-w-0 text-subtle">{setting.label}</dt>
                <dd className="max-w-[55%] text-right font-medium [overflow-wrap:anywhere] tabular-nums">
                  {formatSetting(setting, setting.value, currency)}
                </dd>
              </div>
            );
          })}
        </dl>
        <p className="px-4 py-3 text-xs leading-[1.5] text-pretty text-subtle">
          Changed a setting since? Run the period again to use the new value.
        </p>
      </PopoverContent>
    </Popover>
  );
};
